<template>
    <div id="sell-items">
        <div class="sell-items__top">
            <ItemPanelSearch ref="item-panel-search"
                             @tab="onSearchTabPressed"
                             @enter="debouncedHandleSearchEnter"
                             @change="debouncedHandleSearchChange" />

            <ItemBranchSelector v-if="showItemBranchSelector"
                                :show-label="true" />
        </div>

        <!-- Tabs to switch between item types -->
        <!-- This modifies activeItemTypeTab in Vuex when the tab changes. -->
        <ItemPanelTabs ref="item-panel-tabs"
                       :item-list-collections="itemListCollections"
                       :filtered-item-list-collections="filteredItemListCollectionsByItemType"
                       :search-query="searchQuery" />

        <!-- Items in currently selected item tab -->
        <ItemPanel v-if="itemListCollectionForItemPanel"
                   ref="item-panel"
                   :item-list-collection="itemListCollectionForItemPanel"
                   :item-type="activeItemTypeTab"
                   :search-query="searchQuery"
                   @event-clicked="eventClicked"
                   @item-clicked="itemClicked" />
    </div>
</template>

<script>
import _ from 'lodash';
import EventNames from '@/lib/Events/EventNames';
import ItemBranchSelector from '@/epos/vue/pages/sell/components/ItemBranchSelector';
import ItemPanel from '@/epos/vue/pages/sell/components/ItemPanel';
import ItemPanelSearch from '@/epos/vue/pages/sell/components/ItemPanelSearch';
import ItemPanelTabs from '@/epos/vue/pages/sell/components/ItemPanelTabs';
import { container } from '@/container';
import {
    getVisibleItemListCollectionsByItemType,
    searchCategoryItemListCollection,
} from '@/lib/Items';
import { mapState } from 'vuex';
import { normalizeSearchQuery } from '@/functions/strings';

/**
 * @typedef {import('@/lib/DeviceGroups/DeviceGroup')} DeviceGroup
 * @typedef {import('@/lib/Items/CategoryItemListCollection')} CategoryItemListCollection
 */

export default {
    name: 'SellItems',

    components: {
        ItemBranchSelector,
        ItemPanel,
        ItemPanelSearch,
        ItemPanelTabs,
    },

    data() {
        return {
            /**
             * All of the saleable items organised by item type.
             * There is one CategoryItemListCollection for each item type.
             * This is populated in the create method by getting the value from saleableItemService. It also
             * gets updated if there is an "items synced" event, meaning the item branch was changed.
             *
             * @type {Object<string, CategoryItemListCollection>}
             */
            itemListCollectionsByItemType: {},

            /**
             * All of the saleable items organised by item type and filtered by the current search query
             * (if there is one).
             * There is one CategoryItemListCollection for each item type.
             *
             * @type {Object<string, CategoryItemListCollection>|null}
             */
            filteredItemListCollectionsByItemType: null,

            /**
             * The current search query, populated by listening for events from the ItemPanelSearch component.
             * When this changes the
             */
            searchQuery: '',

            // Populated by a debounced function from lodash in `created`.
            debouncedHandleSearchChange: () => {},
            debouncedHandleSearchEnter: () => {},
        };
    },

    computed: {
        ...mapState('sell', [
            'activeItemTypeTab',
        ]),

        showItemBranchSelector() {
            return container.userService.canChangeItemBranch();
        },

        /**
         * @returns {boolean}
         */
        hasVisibleItems() {
            return container.currentDevice.getDevice()?.deviceGroup?.hasVisibleItems?.() ?? false;
        },

        /**
         * A special computed which collects all possible visible lists in a single object.
         *
         * @returns {{catIDs: number[], eventIDs: number[], itemIDs: number[]}|null}
         */
        visibleLists() {
            if (!this.hasVisibleItems) {
                return null;
            }
            /**
             * @type {DeviceGroup}
             */
            const deviceGroup = container.currentDevice.getDevice()?.deviceGroup || {};
            const {
                visibleCatIDs: catIDs,
                visibleEventIDs: eventIDs,
                visibleItemIDs: itemIDs,
            } = deviceGroup;
            return { catIDs, eventIDs, itemIDs };
        },

        /**
         * @return {Object<string, CategoryItemListCollection>|null}
         */
        visibleItemListCollectionsByItemType() {
            if (!this.visibleLists) {
                return null;
            }
            return getVisibleItemListCollectionsByItemType(
                this.itemListCollectionsByItemType,
                this.visibleLists,
            );
        },

        /**
         * Shows visible collections or default collections.
         *
         * @return {Object<string, CategoryItemListCollection>|null}
         */
        itemListCollections() {
            return this.visibleItemListCollectionsByItemType ?? this.itemListCollectionsByItemType;
        },

        /**
         * @return {CategoryItemListCollection}
         */
        itemListCollectionForItemPanel() {
            if (this.filteredItemListCollectionsByItemType !== null) {
                return this.filteredItemListCollectionsByItemType[this.activeItemTypeTab];
            }

            return this.itemListCollections[this.activeItemTypeTab];
        },
    },

    watch: {
        /**
         * Update search results when the search query changes.
         */
        searchQuery() {
            this.applySearchQuery();
        },
    },

    created() {
        // We need all the items from saleable item service to be ready for searching.
        this.updateItems();
        this.applySearchQuery();

        // Update items/tabs when the item branch changes.
        this.deregisterItemsSyncedListenerFunc = container.$rootScope.$on(EventNames.ITEMS_SYNCED, () => {
            this.updateItems();
            this.applySearchQuery();
        });

        /**
         * Search handling...
         */

        // We debounce handling the 'change' event from the item search box because searching though the items is
        // a computationally intensive operation. We don't need to do it for every letter typed.
        this.debouncedHandleSearchChange = _.debounce((searchQuery) => {
            this.searchQuery = searchQuery;
        }, 100);

        // We debounce handling the 'enter' event from the item search box so that we avoid enter being spammed
        // adding multiple items. It also doubles as a timeout to allow scans to be handled first.
        this.debouncedHandleSearchEnter = _.debounce((searchQuery) => {
            this.searchQuery = searchQuery;

            // Don't do anything when pressing enter in the search box if input was recently handled
            // as a barcode scan. The user will see the scanned text appear in the box momentarily, but that's fine
            // beacuse they did technically type something in.
            if (container.keyboardInputDetector.hasRecentScan()) {
                console.debug('Enter in item search ignored due to recent scan. Clearing value.');
                this.clearSearchQuery();
                return;
            }

            // Enter was pressed in the search box.
            // Select the first item shown on the current item type tab.
            console.debug('Handling enter in search box.');
            if (this.$refs['item-panel'].onSearchEnterPressed()) {
                // Small timeout before clearing for better UX.
                setTimeout(() => {
                    console.debug('Item adding by enter in search box. Cleaning value.');
                    this.clearSearchQuery();
                }, 150);
            }
        }, 200);
    },

    beforeUnmount() {
        if (this.deregisterItemsSyncedListenerFunc) {
            this.deregisterItemsSyncedListenerFunc();
        }
    },

    methods: {
        /**
         * Pull in all the items from the SaleableItemService again.
         * This should happen on load, and if the item branch changes.
         *
         * This happens in a method instead of being a computed property because it needs to be able to update
         * in response to changes outside of Vue.
         * There is a listener added in the `created` method that triggers this.
         */
        updateItems() {
            this.itemListCollectionsByItemType = {}; // Reset it first so Angular updates.
            this.itemListCollectionsByItemType = container.saleableItemService.itemTypes;
        },

        applySearchQuery() {
            const normalizedSearchQuery = this.searchQuery ? normalizeSearchQuery(this.searchQuery) : null;
            if (!normalizedSearchQuery) {
                this.filteredItemListCollectionsByItemType = null;
                return;
            }

            this.filteredItemListCollectionsByItemType = {};
            Object.keys(this.itemListCollections).forEach((itemType) => {
                const itemListCollection = this.itemListCollections[itemType];
                this.filteredItemListCollectionsByItemType[itemType] = searchCategoryItemListCollection(
                    itemListCollection,
                    normalizedSearchQuery,
                );
            });
        },

        eventClicked({ event, category }) {
            // In future, when Vue is the boss, we'll start the event booking flow from here.
            // For now defer it to Angular.
            container.eventBus.broadcast(
                EventNames.START_EVENT_BOOKING_REQUESTED,
                {
                    event,
                    category,
                }
            );
        },

        itemClicked({ item }) {
            // In future, when Vue is the boss, we'll start the item booking flow from here.
            // For now defer it to Angular.
            container.eventBus.broadcast(
                EventNames.ADD_ITEM_TO_CART_REQUESTED,
                {
                    item,
                    quantity: 1,
                    extraInfo: null,
                    triggeredByItemButton: true,
                }
            );
        },

        /**
         * Clear the value typed in the item search box.
         */
        clearSearchQuery() {
            // Clear our copy of the value.
            // The component will emit a change event when it clears, but that has a delay. We'll still handle that
            // event but do this faster here too.
            this.searchQuery = '';
            this.$refs['item-panel-search'].clear();
        },

        onSearchTabPressed() {
            // Switch between item type tabs.
            this.$refs['item-panel-tabs'].selectNextTab();
        },
    },
};
</script>

<style lang="less">
@import "../../../../../less/variables";

#sell-items {
    flex-grow: 1;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.sell-items__top {
    display: flex;
    align-items: stretch;
    grid-gap: 10px;
    padding: 8px 6px;
    color: @white;
}
</style>
